/**
 * API Code Examples for different programming languages
 * Used in the API documentation component
 */

export const getCodeExamples = (userInfo) => ({
  curl: `curl --location 'https://api.easyappicon.com/generate' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: YOUR_API_KEY' \\
--data '{
    "uploadedImage": "data:image/png;base64,YOUR_BASE64_IMAGE",
    "userId": "${userInfo?.id || '{USER_ID_HERE}'}",
    "backgroundColor": "#000000",
    "iconName": "ic_launcher",
    "padding": 0,
    "showBadge": false,
    "badgeText": "",
    "badgeColor": "#000000",
    "format": "png"
  }'`,

  javascript: `// Requires Node.js 18+
import fs from 'fs';

const response = await fetch('https://api.easyappicon.com/generate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    uploadedImage: 'data:image/png;base64,YOUR_BASE64_IMAGE',
    userId: '${userInfo?.id || '{USER_ID_HERE}'}',
    backgroundColor: '#000000',
    iconName: 'ic_launcher',
    padding: 0,
    showBadge: false,
    badgeText: '',
    badgeColor: '#000000',
    format: 'png'
  })
});

if (!response.ok) {
  throw new Error(\`API Error (\${response.status}): \${await response.text()}\`);
}

const contentType = response.headers.get('content-type');

if (contentType?.includes('application/zip')) {
  // Binary ZIP response (downloadUrl: false)
  const buffer = Buffer.from(await response.arrayBuffer());
  fs.writeFileSync('icons.zip', buffer);
  console.log('Saved icons.zip');

} else if (contentType?.includes('application/json')) {
  // JSON response with presigned download URL (downloadUrl: true)
  const { downloadUrl, filename } = await response.json();
  const file = await fetch(downloadUrl);
  const buffer = Buffer.from(await file.arrayBuffer());
  fs.writeFileSync(filename, buffer);
  console.log(\`Saved \${filename}\`);
}`,

  python: `# pip install requests
import requests

response = requests.post('https://api.easyappicon.com/generate',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'YOUR_API_KEY'
    },
    json={
        'uploadedImage': 'data:image/png;base64,YOUR_BASE64_IMAGE',
        'userId': '${userInfo?.id || '{USER_ID_HERE}'}',
        'backgroundColor': '#000000',
        'iconName': 'ic_launcher',
        'padding': 0,
        'showBadge': False,
        'badgeText': '',
        'badgeColor': '#000000',
        'format': 'png'
    }
)

response.raise_for_status()

content_type = response.headers.get('Content-Type', '')

if 'application/zip' in content_type:
    # Binary ZIP response (downloadUrl: false)
    with open('icons.zip', 'wb') as f:
        f.write(response.content)
    print('Saved icons.zip')

elif 'application/json' in content_type:
    # JSON response with presigned download URL (downloadUrl: true)
    data = response.json()
    file = requests.get(data['downloadUrl'])
    with open(data['filename'], 'wb') as f:
        f.write(file.content)
    print(f"Saved {data['filename']}")
`,

  php: `<?php
// Requires PHP with cURL extension
$ch = curl_init('https://api.easyappicon.com/generate');

curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode([
        'uploadedImage' => 'data:image/png;base64,YOUR_BASE64_IMAGE',
        'userId' => '${userInfo?.id || '{USER_ID_HERE}'}',
        'backgroundColor' => '#000000',
        'iconName' => 'ic_launcher',
        'padding' => 0,
        'showBadge' => false,
        'badgeText' => '',
        'badgeColor' => '#000000',
        'format' => 'png'
    ]),
    CURLOPT_HTTPHEADER => [
        'Content-Type: application/json',
        'Authorization: YOUR_API_KEY'
    ],
    CURLOPT_RETURNTRANSFER => true,
]);

$body = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
$contentType = curl_getinfo($ch, CURLINFO_CONTENT_TYPE);
curl_close($ch);

if ($httpCode !== 200) {
    die("API Error ($httpCode): $body");
}

if (str_contains($contentType, 'application/zip')) {
    // Binary ZIP response (downloadUrl: false)
    file_put_contents('icons.zip', $body);
    echo "Saved icons.zip\\n";

} elseif (str_contains($contentType, 'application/json')) {
    // JSON response with presigned download URL (downloadUrl: true)
    $data = json_decode($body, true);
    $file = file_get_contents($data['downloadUrl']);
    file_put_contents($data['filename'], $file);
    echo "Saved " . $data['filename'] . "\\n";
}
?>`,

  csharp: `// Requires .NET 6+
using var client = new HttpClient();

var payload = new {
    uploadedImage = "data:image/png;base64,YOUR_BASE64_IMAGE",
    userId = "${userInfo?.id || '{USER_ID_HERE}'}",
    backgroundColor = "#000000",
    iconName = "ic_launcher",
    padding = 0,
    showBadge = false,
    badgeText = "",
    badgeColor = "#000000",
    format = "png"
};

var request = new HttpRequestMessage(HttpMethod.Post, "https://api.easyappicon.com/generate");
request.Headers.Add("Authorization", "YOUR_API_KEY");
request.Content = new StringContent(
    System.Text.Json.JsonSerializer.Serialize(payload),
    System.Text.Encoding.UTF8, "application/json");

var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();

var contentType = response.Content.Headers.ContentType?.MediaType ?? "";

if (contentType.Contains("application/zip"))
{
    // Binary ZIP response (downloadUrl: false)
    var bytes = await response.Content.ReadAsByteArrayAsync();
    await File.WriteAllBytesAsync("icons.zip", bytes);
    Console.WriteLine("Saved icons.zip");
}
else if (contentType.Contains("application/json"))
{
    // JSON response with presigned download URL (downloadUrl: true)
    var json = System.Text.Json.JsonDocument.Parse(
        await response.Content.ReadAsStringAsync());
    var downloadUrl = json.RootElement.GetProperty("downloadUrl").GetString()!;
    var filename = json.RootElement.GetProperty("filename").GetString()!;
    var fileBytes = await client.GetByteArrayAsync(downloadUrl);
    await File.WriteAllBytesAsync(filename, fileBytes);
    Console.WriteLine($"Saved {filename}");
}`,

  ruby: `# Ruby 2.5+ (stdlib only)
require 'net/http'
require 'json'

uri = URI('https://api.easyappicon.com/generate')
request = Net::HTTP::Post.new(uri, {
  'Content-Type' => 'application/json',
  'Authorization' => 'YOUR_API_KEY'
})

request.body = {
  uploadedImage: 'data:image/png;base64,YOUR_BASE64_IMAGE',
  userId: '${userInfo?.id || '{USER_ID_HERE}'}',
  backgroundColor: '#000000',
  iconName: 'ic_launcher',
  padding: 0,
  showBadge: false,
  badgeText: '',
  badgeColor: '#000000',
  format: 'png'
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http|
  http.request(request)
}

raise "API Error (#{response.code}): #{response.body}" unless response.is_a?(Net::HTTPSuccess)

if response['content-type']&.include?('application/zip')
  # Binary ZIP response (downloadUrl: false)
  File.binwrite('icons.zip', response.body)
  puts 'Saved icons.zip'

elsif response['content-type']&.include?('application/json')
  # JSON response with presigned download URL (downloadUrl: true)
  data = JSON.parse(response.body)
  file = Net::HTTP.get(URI(data['downloadUrl']))
  File.binwrite(data['filename'], file)
  puts "Saved #{data['filename']}"
end`,

  go: '// Go 1.16+\n' +
'package main\n' +
'\n' +
'import (\n' +
'    "encoding/json"\n' +
'    "fmt"\n' +
'    "io"\n' +
'    "net/http"\n' +
'    "os"\n' +
'    "strings"\n' +
')\n' +
'\n' +
'func main() {\n' +
'    payload := `{\n' +
'        "uploadedImage": "data:image/png;base64,YOUR_BASE64_IMAGE",\n' +
`        "userId": "${userInfo?.id || '{USER_ID_HERE}'}",\n` +
'        "backgroundColor": "#000000",\n' +
'        "iconName": "ic_launcher",\n' +
'        "padding": 0,\n' +
'        "showBadge": false,\n' +
'        "badgeText": "",\n' +
'        "badgeColor": "#000000",\n' +
'        "format": "png"\n' +
'    }`\n' +
'\n' +
'    req, _ := http.NewRequest("POST", "https://api.easyappicon.com/generate",\n' +
'        strings.NewReader(payload))\n' +
'    req.Header.Set("Content-Type", "application/json")\n' +
'    req.Header.Set("Authorization", "YOUR_API_KEY")\n' +
'\n' +
'    resp, err := http.DefaultClient.Do(req)\n' +
'    if err != nil {\n' +
'        panic(err)\n' +
'    }\n' +
'    defer resp.Body.Close()\n' +
'\n' +
'    if resp.StatusCode != 200 {\n' +
'        body, _ := io.ReadAll(resp.Body)\n' +
'        panic(fmt.Sprintf("API Error (%d): %s", resp.StatusCode, body))\n' +
'    }\n' +
'\n' +
'    ct := resp.Header.Get("Content-Type")\n' +
'\n' +
'    if strings.Contains(ct, "application/zip") {\n' +
'        // Binary ZIP response (downloadUrl: false)\n' +
'        data, _ := io.ReadAll(resp.Body)\n' +
'        os.WriteFile("icons.zip", data, 0644)\n' +
'        fmt.Println("Saved icons.zip")\n' +
'\n' +
'    } else if strings.Contains(ct, "application/json") {\n' +
'        // JSON response with presigned download URL (downloadUrl: true)\n' +
'        var result struct {\n' +
'            DownloadUrl string `json:"downloadUrl"`\n' +
'            Filename    string `json:"filename"`\n' +
'        }\n' +
'        json.NewDecoder(resp.Body).Decode(&result)\n' +
'        dlResp, _ := http.Get(result.DownloadUrl)\n' +
'        defer dlResp.Body.Close()\n' +
'        fileData, _ := io.ReadAll(dlResp.Body)\n' +
'        os.WriteFile(result.Filename, fileData, 0644)\n' +
'        fmt.Printf("Saved %s\\n", result.Filename)\n' +
'    }\n' +
'}'
});
